import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TS1_REMINDER_NOTES_MAX_LENGTH } from "src/app/cbta-web/forms/ts1-v2/ts1-form.factories";
import { Ts1ReminderNotesModalComponent } from "src/app/cbta-web/forms/ts1-v2/ts1-reminder-notes-modal/ts1-reminder-notes-modal.component";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	ManeuverOutcomeDraft,
	ManeuverOutcomeValue,
	OBComments,
	ResetManeuverItem,
	TemCompetencyOutcomeDraft,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { ContainerRatingsService } from "../../../../services/base/container-ratings.services";
import {
	filterTs1Step2SummaryComments,
	isTs1InstructorCommentVisible,
} from "../../../preview/ts1-preview-summary-comments.utils";
import { getSafeCandidateRating } from "../../../utils/ts1-rating-ob-interaction";
import {
	buildOutcomeSummaryComment,
	buildTemCompetencySummaryComment,
	MANEUVER_OUTCOME_NA_COMMENT,
	openOutcomeCommentModal,
	outcomeCommentField,
	outcomeRatingRequiresComment,
	OutcomeSlot,
	outcomeSlotLabel,
	RATING_PER_COMPETENCY_LABEL,
} from "../../../utils/ts1-rating-outcome-interaction";
import {
	step3PfColumnOutcomeSlot,
	step3PmColumnOutcomeSlot,
	TS1_REVIEW_RATING_LEGEND,
	ts1ReviewCellClass,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
	Ts1ReviewRatingCell,
} from "../../../utils/ts1-rating-review.utils";
import { isTs1SummaryObCommentMissing, Step3ReviewGapSection } from "../../../utils/ts1-rating-step3-validation.utils";
import {
	formatTs1SummaryCompetencyStatusLabel,
	formatTs1SummaryManeuverLabel,
} from "../../../utils/ts1-summary-display.utils";
import {
	clearLegacyTemCompetencySlotsForCandidate,
	temCompetencySlotForCandidateIndex,
} from "../../../utils/ts1-tem-competency-slots.utils";

@Component({
	selector: "app-ts1-rating-step3-review",
	templateUrl: "./ts1-rating-step3-review.component.html",
	styleUrls: ["./ts1-rating-step3-review.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingStep3ReviewComponent {
	@Input() candidateNames: string[] = [];
	@Input() candidateCrewRoles: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];
	@Input() candidateIds: string[] = [];
	@Input() currentIndex = 0;
	@Input() competencyBlocks: Ts1ReviewCompetencyBlock[] = [];
	@Input() maneuverRows: Ts1ReviewManeuverRow[] = [];
	@Input() maneuverOutcomes: ManeuverOutcomeDraft[] = [];
	@Input() temCompetencyOutcomes: TemCompetencyOutcomeDraft[] = [];
	@Input() summaryComments: OBComments[] = [];
	@Input() traineeAssessmentId = "";
	/** Per-candidate sign-off form group (recommendation + overall grading). */
	@Input() signOffControl: UntypedFormGroup | null = null;
	@Input() reminderNotesEnabled = false;
	@Input() useStructuredReminderNotes = false;
	@Input() reminderNoteResponses: any = null;
	/** Preview Summary — display-only, no edits or modals. */
	@Input() readOnly = false;
	/** Persistent highlight for the checklist item the instructor jumped to. */
	@Input() scrollHighlightKey: string | null = null;

	@Output() prevCandidate = new EventEmitter<void>();
	@Output() nextCandidate = new EventEmitter<void>();
	@Output() maneuverOutcomesChange = new EventEmitter<ManeuverOutcomeDraft[]>();
	@Output() temCompetencyOutcomesChange = new EventEmitter<TemCompetencyOutcomeDraft[]>();
	@Output() outcomeComment = new EventEmitter<OBComments>();
	@Output() summaryEdited = new EventEmitter<void>();
	@Output() recommendationChanged = new EventEmitter<void>();

	readonly CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;
	readonly ratingLegendItems = TS1_REVIEW_RATING_LEGEND;
	readonly ratingOptions: Array<number | "na"> = [1, 2, 3, 4, 5, "na"];
	readonly competencyRatingOptions: number[] = [1, 2, 3, 4, 5];
	readonly gradingOptions: number[] = [1, 2, 3, 4, 5];
	readonly hasInstructorComment = isTs1InstructorCommentVisible;
	readonly isMissingRequiredComment = isTs1SummaryObCommentMissing;

	maneuverRatingsExpanded = true;

	private isDialogOpen = false;

	constructor(
		private dialog: MatDialog,
		private containerRatingsService: ContainerRatingsService,
	) {}

	toggleManeuverRatings(): void {
		this.maneuverRatingsExpanded = !this.maneuverRatingsExpanded;
	}

	prepareScrollTarget(section: Step3ReviewGapSection, field?: string): void {
		if (section === "maneuver") {
			this.maneuverRatingsExpanded = true;
		}
		if (section === "reminderNotes" && field) {
			this.markReminderNoteStatusTouched(field);
		}
	}

	get reminderNoteScrollHighlightId(): string | null {
		const key = this.scrollHighlightKey;
		if (!key?.startsWith("reminderNotes:")) return null;
		const noteId = key.slice("reminderNotes:".length).trim();
		return noteId || null;
	}

	private markReminderNoteStatusTouched(noteId: string): void {
		if (!noteId || noteId === "reminderStatus" || !(this.reminderNoteResponses instanceof UntypedFormArray)) {
			return;
		}

		const group = this.reminderNoteResponses.controls.find(
			(ctrl) => String(ctrl.get("noteId")?.value ?? "").trim() === noteId,
		) as UntypedFormGroup | undefined;
		const status = group?.get("status");
		status?.markAsTouched();
		status?.updateValueAndValidity();
	}

	isScrollHighlightActive(section: string, field?: string): boolean {
		if (!this.scrollHighlightKey) return false;
		if (field) {
			return this.scrollHighlightKey === `${section}:${field}`;
		}
		if (this.scrollHighlightKey === section) return true;
		if (section === "outcome" && this.scrollHighlightKey === "outcome:outcome") return true;
		if (section === "reminderNotes" && this.scrollHighlightKey.startsWith("reminderNotes:")) return true;
		return false;
	}

	get displaySummaryComments(): OBComments[] {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		return filterTs1Step2SummaryComments(this.summaryComments, cr.addRemoveOb);
	}

	summaryManeuverLabel(comment: OBComments): string {
		return formatTs1SummaryManeuverLabel(comment, this.resetManeuvers);
	}

	summaryCompetencyStatusLabel(comment: OBComments): string {
		return formatTs1SummaryCompetencyStatusLabel(comment, this.resetManeuvers);
	}

	private get resetManeuvers(): ResetManeuverItem[] {
		return getSafeCandidateRating(this.containerRatingsService.draft)?.resetManeuvers ?? [];
	}

	get hasSecondCandidate(): boolean {
		return this.candidateNames.length >= 2;
	}

	get currentName(): string {
		return this.candidateNames[this.currentIndex] ?? "";
	}

	get currentRole(): CandidateRole {
		return this.candidateCrewRoles[this.currentIndex] ?? CandidateRole.PF;
	}

	get currentRoleLabel(): string {
		return this.roleLabel(this.currentRole);
	}

	get generalObservationText(): string {
		return String(this.signOffControl?.get("generalObservation")?.value ?? "").trim();
	}

	get reminderNotesText(): string {
		return String(this.signOffControl?.get("reminderNotes")?.value ?? "").trim();
	}

	openGeneralObservationEditor(): void {
		this.openReviewNotesEditor("General Observation", "generalObservation");
	}

	openReminderNotesEditor(): void {
		this.openReviewNotesEditor("Additional Training/Assessment Details", "reminderNotes");
	}

	private openReviewNotesEditor(title: string, controlName: "generalObservation" | "reminderNotes"): void {
		if (this.readOnly || !this.signOffControl) return;

		const dialogRef = this.dialog.open(Ts1ReminderNotesModalComponent, {
			panelClass: ["ts1-reminder-notes-modal-panel", "ts1-reminder-notes-modal"],
			width: "720px",
			hasBackdrop: true,
			data: {
				title,
				value: this.signOffControl.get(controlName)?.value ?? "",
				placeholder: controlName === "generalObservation" ? "Enter general observation..." : "Enter reminder notes...",
				commentAiAssist: true,
			},
		});

		dialogRef.afterClosed().subscribe((result: string | null) => {
			if (result === null) return;
			const control = this.signOffControl?.get(controlName);
			control?.setValue(result.slice(0, TS1_REMINDER_NOTES_MAX_LENGTH));
			control?.markAsDirty();
			this.recommendationChanged.emit();
		});
	}

	getOutcomeRateClass(rate: number | undefined): string {
		if (rate === 1) return "text-red";
		if (rate === 2) return "text-yellow";
		if (rate === 3) return "text-yellow-60";
		if (rate === 4) return "text-green";
		if (rate === 5) return "text-green-90";
		return "text-grey-60";
	}

	getCommentCandidateLabel(comment: OBComments): string {
		return this.candidateSlotLabel(this.candidateIndexForId(comment.candidateId));
	}

	getCommentCandidateLabelClass(comment: OBComments): string {
		const idx = this.candidateIndexForId(comment.candidateId);
		if (idx === 0) return "ts1-summary-candidate-label--1";
		if (idx === 1) return "ts1-summary-candidate-label--2";
		return "";
	}

	private candidateIndexForId(candidateId: string | undefined): number {
		if (!candidateId) return this.currentIndex;
		const idx = this.candidateIds.indexOf(candidateId);
		return idx >= 0 ? idx : this.currentIndex;
	}

	private candidateSlotLabel(index: number): string {
		if (index < 0) return "";
		if (index === 0) return "Candidate 1";
		if (index === 1) return "Candidate 2";
		return `Candidate ${index + 1}`;
	}

	private roleLabel(role: CandidateRole | undefined): string {
		if (role === CandidateRole.PF) return "PF";
		if (role === CandidateRole.PM) return "PM";
		return "";
	}

	viewSummaryModal(): void {
		if (this.readOnly) return;

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "view-rating-summary-modal",
			height: "560px",
			maxHeight: "90vh",
			width: "1154px",
			maxWidth: "95vw",
			hasBackdrop: true,
			data: {
				modalTitle: "Comments",
				modalName: "viewTs1V3SummaryModal",
				modalContent: this.displaySummaryComments,
				modalTraineeAssessmentId: this.traineeAssessmentId,
				modalReviewLayout: true,
				modalCandidateId: this.candidateIds[this.currentIndex] ?? "",
				noCloseBtn: true,
				modalBtnConfirmText: "Close",
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.summaryEdited.emit();
		});
	}

	cellClass = ts1ReviewCellClass;

	optionLabel(opt: number | "na" | "inc"): string {
		if (opt === "na") return "N/A";
		if (opt === "inc") return "Inc.";
		return String(opt);
	}

	competencyOptionLabel(opt: number): string {
		return String(opt);
	}

	competencyRatingAsNumber(value: Ts1ReviewRatingCell): number | null {
		if (value == null || value === "na" || value === "inc") return null;
		return value;
	}

	isSelected(selected: number | "na" | "inc" | null, opt: number | "na" | "inc"): boolean {
		if (selected == null) return false;
		return selected === opt;
	}

	isCompetencySelected(selected: number | null, opt: number): boolean {
		if (selected == null) return false;
		return selected === opt;
	}

	get currentCandidateId(): string {
		return this.candidateIds[this.currentIndex] ?? "";
	}

	pfSlot(): OutcomeSlot {
		return step3PfColumnOutcomeSlot(this.currentIndex);
	}

	pmSlot(): OutcomeSlot {
		return step3PmColumnOutcomeSlot(this.currentIndex, this.hasSecondCandidate);
	}

	isPfColumnInteractive(): boolean {
		return !!this.currentCandidateId;
	}

	isPmColumnInteractive(): boolean {
		return !!this.currentCandidateId;
	}

	getOutcome(maneuverCode: string): ManeuverOutcomeDraft {
		return this.maneuverOutcomes.find((o) => o.maneuverCode === maneuverCode) ?? { maneuverCode };
	}

	maneuverCellValue(row: Ts1ReviewManeuverRow, column: "pf" | "pm"): number | "na" | "inc" | null {
		return column === "pf" ? row.pf : row.pm;
	}

	isManeuverColumnEditable(_column: "pf" | "pm"): boolean {
		return this.isCurrentCandidateEditable();
	}

	maneuverCellClasses(
		row: Ts1ReviewManeuverRow,
		column: "pf" | "pm",
		opt: number | "na" | "inc",
	): Record<string, boolean> {
		const selected = this.maneuverCellValue(row, column);
		if (!this.isSelected(selected, opt)) return {};
		if (opt === "na") {
			return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-na": true };
		}
		if (opt === "inc") {
			return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-inc": true };
		}
		return {
			"ts1-review-grid__cell--selected": true,
			[`ts1-review-grid__cell--rate-${opt}`]: true,
		};
	}

	selectManeuverRating(row: Ts1ReviewManeuverRow, column: "pf" | "pm", value: ManeuverOutcomeValue): void {
		if (!this.isManeuverColumnEditable(column)) return;

		const slot = column === "pf" ? this.pfSlot() : this.pmSlot();
		const outcome = this.getOutcome(row.maneuverCode);
		const current = outcome[slot];
		const isSame = current === value;

		if (isSame && !outcomeRatingRequiresComment(value)) return;

		if (outcomeRatingRequiresComment(value)) {
			if (this.isDialogOpen) return;
			void this.promptCommentAndApply(row, slot, value, isSame);
			return;
		}

		this.applySelection(row, slot, value, "");
	}

	private async promptCommentAndApply(
		row: Ts1ReviewManeuverRow,
		slot: OutcomeSlot,
		value: ManeuverOutcomeValue,
		allowSameValue: boolean,
	): Promise<void> {
		const outcome = this.getOutcome(row.maneuverCode);
		if (!allowSameValue && outcome[slot] === value) return;

		this.isDialogOpen = true;
		const commentKey = outcomeCommentField(slot);
		const existingComment = (outcome[commentKey] as string | undefined) ?? "";

		const result = await openOutcomeCommentModal(this.dialog, {
			maneuverLabel: row.label,
			slotLabel: outcomeSlotLabel(slot),
			value,
			existingComment,
		});

		this.isDialogOpen = false;

		if (result?.comment != null) {
			this.applySelection(row, slot, value, result.comment);
		}
	}

	private applySelection(
		row: Ts1ReviewManeuverRow,
		slot: OutcomeSlot,
		value: ManeuverOutcomeValue,
		instructorComment: string,
	): void {
		const outcome = this.getOutcome(row.maneuverCode);
		const commentKey = outcomeCommentField(slot);
		const next: ManeuverOutcomeDraft = {
			...outcome,
			[slot]: value,
			[commentKey]: outcomeRatingRequiresComment(value) ? instructorComment : undefined,
		};

		const updated = [...this.maneuverOutcomes];
		const idx = updated.findIndex((o) => o.maneuverCode === row.maneuverCode);
		if (idx >= 0) updated[idx] = next;
		else updated.push(next);

		this.maneuverOutcomesChange.emit(updated);

		const candidateId = this.candidateIdForSlot(slot);
		if (!candidateId) return;

		const resolvedComment =
			value === "na" ? MANEUVER_OUTCOME_NA_COMMENT : outcomeRatingRequiresComment(value) ? instructorComment : "";

		this.outcomeComment.emit(
			buildOutcomeSummaryComment({
				maneuverLabel: row.label,
				maneuverCode: row.maneuverCode,
				slot,
				slotLabel: outcomeSlotLabel(slot),
				value,
				instructorComment: resolvedComment,
				candidateId,
			}),
		);
	}

	private candidateIdForSlot(_slot: OutcomeSlot): string {
		return this.currentCandidateId;
	}

	getTemOutcome(competencyCode: string): TemCompetencyOutcomeDraft {
		return this.temCompetencyOutcomes.find((o) => o.competencyCode === competencyCode) ?? { competencyCode };
	}

	/** Canonical TEM Rating C slot for the carousel-selected candidate. */
	currentTemSlot(): OutcomeSlot {
		return temCompetencySlotForCandidateIndex(this.currentIndex, this.hasSecondCandidate);
	}

	isCurrentCandidateEditable(): boolean {
		return !!this.currentCandidateId && !this.readOnly;
	}

	/** Highlight class for a read-only pre-calculated rating cell (A or B). */
	readonlyCellClasses(value: Ts1ReviewRatingCell, opt: number | "na" | "inc"): Record<string, boolean> {
		if (!this.isSelected(value, opt)) return {};
		if (opt === "na") {
			return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-na": true };
		}
		if (opt === "inc") {
			return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-inc": true };
		}
		return {
			"ts1-review-grid__cell--selected": true,
			[`ts1-review-grid__cell--rate-${opt}`]: true,
		};
	}

	competencyCellClasses(block: Ts1ReviewCompetencyBlock, opt: number): Record<string, boolean> {
		return this.competencyReadonlyCellClasses(block.ratingC, opt);
	}

	competencyReadonlyCellClasses(value: Ts1ReviewRatingCell, opt: number): Record<string, boolean> {
		if (value == null || value === "na" || value === "inc") return {};
		return this.competencyReadonlyCellClassesForNumber(value, opt);
	}

	private competencyReadonlyCellClassesForNumber(value: number | null, opt: number): Record<string, boolean> {
		if (!this.isCompetencySelected(value, opt)) return {};
		return {
			"ts1-review-grid__cell--selected": true,
			[`ts1-review-grid__cell--rate-${opt}`]: true,
		};
	}

	/** FINAL SCORE panel styling — rating colors when set, white when pending or zero. */
	finalScorePanelClasses(block: Ts1ReviewCompetencyBlock): Record<string, boolean> {
		if (block.notGraded) {
			return { "ts1-review-comp__score-panel--zero": true };
		}
		const value = block.finalScore;
		if (value == null || value === 0 || value === "na" || value === "inc") {
			return { "ts1-review-comp__score-panel--zero": true };
		}
		return {
			[`ts1-review-comp__score-panel--rate-${value}`]: true,
		};
	}

	isCompetencyFinalScorePending(block: Ts1ReviewCompetencyBlock): boolean {
		return !block.notGraded && block.finalScore == null;
	}

	finalScoreLabel(block: Ts1ReviewCompetencyBlock): string {
		if (block.notGraded) return "0";
		if (block.finalScore == null || block.finalScore === "na" || block.finalScore === "inc") return "No score";
		return String(block.finalScore);
	}

	selectCompetencyRating(block: Ts1ReviewCompetencyBlock, value: number): void {
		if (this.readOnly) return;

		const competencyCode = block.code;
		if (!competencyCode || block.notGraded) return;
		if (!this.isCurrentCandidateEditable()) return;

		const slot = this.currentTemSlot();
		const outcome = this.getTemOutcome(competencyCode);
		const current = outcome[slot];
		const isSame = current === value;

		if (isSame && !outcomeRatingRequiresComment(value)) return;

		if (outcomeRatingRequiresComment(value)) {
			if (this.isDialogOpen) return;
			void this.promptTemCommentAndApply(competencyCode, slot, value, isSame);
			return;
		}

		this.applyTemSelection(competencyCode, slot, value, "");
	}

	private async promptTemCommentAndApply(
		competencyCode: string,
		slot: OutcomeSlot,
		value: ManeuverOutcomeValue,
		allowSameValue: boolean,
	): Promise<void> {
		const outcome = this.getTemOutcome(competencyCode);
		if (!allowSameValue && outcome[slot] === value) return;

		this.isDialogOpen = true;
		const commentKey = outcomeCommentField(slot);
		const existingComment = (outcome[commentKey as keyof TemCompetencyOutcomeDraft] as string | undefined) ?? "";

		const result = await openOutcomeCommentModal(this.dialog, {
			maneuverLabel: competencyCode,
			value,
			existingComment,
			typeLabel: RATING_PER_COMPETENCY_LABEL,
		});

		this.isDialogOpen = false;

		if (result?.comment != null) {
			this.applyTemSelection(competencyCode, slot, value, result.comment);
		}
	}

	private applyTemSelection(
		competencyCode: string,
		slot: OutcomeSlot,
		value: ManeuverOutcomeValue,
		instructorComment: string,
	): void {
		const outcome = this.getTemOutcome(competencyCode);
		const commentKey = outcomeCommentField(slot);
		const next: TemCompetencyOutcomeDraft = clearLegacyTemCompetencySlotsForCandidate(
			{
				...outcome,
				[slot]: value,
				[commentKey as keyof TemCompetencyOutcomeDraft]: outcomeRatingRequiresComment(value)
					? instructorComment
					: undefined,
			},
			this.currentIndex,
		);

		const updated = [...this.temCompetencyOutcomes];
		const idx = updated.findIndex((o) => o.competencyCode === competencyCode);
		if (idx >= 0) updated[idx] = next;
		else updated.push(next);

		this.temCompetencyOutcomesChange.emit(updated);

		const candidateId = this.candidateIdForSlot(slot);
		if (!candidateId) return;

		this.outcomeComment.emit(
			buildTemCompetencySummaryComment({
				competencyCode,
				slot,
				slotLabel: outcomeSlotLabel(slot),
				value,
				instructorComment: outcomeRatingRequiresComment(value) ? instructorComment : "",
				candidateId,
			}),
		);
	}

	// ---- Recommendation by Instructor + Overall Grading ----

	private recommendationValue<T>(controlName: string): T | null {
		return (this.signOffControl?.get(controlName)?.value ?? null) as T | null;
	}

	/** Unique per candidate — quick summary keeps multiple review panels in the DOM. */
	recommendationFieldName(field: string): string {
		const scope = this.traineeAssessmentId || this.currentCandidateId || `candidate-${this.currentIndex}`;
		return `ts1-reco-${scope}-${field}`;
	}

	private matchesRecommendationValue(current: unknown, expected: boolean): boolean {
		if (expected) {
			return current === true || current === "true" || current === "Yes" || current === "yes";
		}
		return current === false || current === "false" || current === "No" || current === "no";
	}

	get recommendationCategory(): string {
		return this.recommendationValue<string>("category") ?? "";
	}

	get isTrainingCategory(): boolean {
		return this.recommendationCategory !== "Check";
	}

	get sessionCompletedLabel(): string {
		return this.isTrainingCategory ? "Training Session Completed" : "Check Session Completed";
	}

	get objectivesLabel(): string {
		return this.isTrainingCategory ? "Training Objectives Are Met" : "Check Objectives Are Met";
	}

	setTrainingCategory(isTraining: boolean): void {
		if (this.readOnly) return;

		const category = isTraining ? "Training" : "Check";
		const categoryControl = this.signOffControl?.get("category");
		if (!categoryControl) return;
		categoryControl.setValue(category);
		categoryControl.markAsDirty();

		const clearanceControl = this.signOffControl?.get("clearanceCheck");
		if (!isTraining) {
			clearanceControl?.setValue("N/A");
			clearanceControl?.markAsDirty();
		} else if (clearanceControl?.value === "N/A") {
			clearanceControl.setValue(null);
			clearanceControl.markAsDirty();
		}

		this.recommendationChanged.emit();
	}

	isRecommendationSelected(controlName: string, value: boolean): boolean {
		return this.matchesRecommendationValue(this.recommendationValue(controlName), value);
	}

	isClearanceSelected(value: boolean | string): boolean {
		const current = this.recommendationValue<boolean | string>("clearanceCheck");
		if (value === true) return this.matchesRecommendationValue(current, true);
		if (value === false) return this.matchesRecommendationValue(current, false);
		if (value === "N/A") {
			return current === "N/A" || current === "n_a" || current === "n/a";
		}
		return current === value;
	}

	setRecommendation(controlName: string, value: boolean | string): void {
		if (this.readOnly) return;

		const control = this.signOffControl?.get(controlName);
		if (!control) return;
		control.setValue(value);
		control.markAsDirty();
		this.recommendationChanged.emit();
	}

	get clearedForCheckVisible(): boolean {
		return this.isTrainingCategory;
	}

	get overallGrading(): number | null {
		const value = this.recommendationValue<number>("finalRating");
		return value && value > 0 ? value : null;
	}

	isGradingSelected(value: number): boolean {
		return this.overallGrading === value;
	}

	gradingCellClasses(grade: number): Record<string, boolean> {
		const selected = this.isGradingSelected(grade);
		return {
			"ts1-review-grading__cell--selected": selected,
			"ts1-review-grading__cell--rate-1": selected && grade === 1,
			"ts1-review-grading__cell--rate-2": selected && grade === 2,
			"ts1-review-grading__cell--rate-3": selected && grade === 3,
			"ts1-review-grading__cell--rate-4": selected && grade === 4,
			"ts1-review-grading__cell--rate-5": selected && grade === 5,
		};
	}

	selectOverallGrading(value: number): void {
		if (this.readOnly) return;

		const control = this.signOffControl?.get("finalRating");
		if (!control) return;
		control.setValue(value);
		control.markAsDirty();
		this.recommendationChanged.emit();
	}

	onReminderNoteStatusChanged(): void {
		this.recommendationChanged.emit();
	}
}
